import { CPagination, CPaginationItem } from '@coreui/react-pro'
import React, { useMemo } from 'react'
import { reduxServices } from '../../reducers/reduxServices'
import { useAppDispatch } from '../../stateStore'

const OPagination = ({
  currentPage,
  pageSetter,
  paginationRange,
  disabled = false,
}: {
  currentPage: number
  pageSetter: React.Dispatch<React.SetStateAction<number>>
  paginationRange: number[]
  disabled?: boolean
}): JSX.Element => {
  const dispatch = useAppDispatch()
  const persistCurrentPage = (pageNumber: number) => {
    dispatch(reduxServices.app.actions.setPersistCurrentPage(pageNumber))
  }

  const handleNextPage = () => {
    if (disabled) return
    pageSetter(currentPage + 1)
    persistCurrentPage(currentPage + 1)
    window?.scrollTo(0, 0)
    localStorage.setItem('isViewReport', 'false')
  }

  const handlePreviousPage = () => {
    if (disabled) return
    pageSetter(currentPage - 1)
    persistCurrentPage(currentPage - 1)
    window?.scrollTo(0, 0)
    localStorage.setItem('isViewReport', 'false')
  }

  const handleFirstPage = () => {
    if (disabled) return
    pageSetter(1)
    persistCurrentPage(1)
    window?.scrollTo(0, 0)
    localStorage.setItem('isViewReport', 'false')
  }

  const handleLastPage = () => {
    if (disabled) return
    pageSetter(paginationRange[paginationRange.length - 1])
    persistCurrentPage(paginationRange[paginationRange.length - 1])
    window?.scrollTo(0, 0)
    localStorage.setItem('isViewReport', 'false')
  }

  const handleSelectPage = (pageNumber: number) => {
    if (disabled) return
    pageSetter(pageNumber)
    persistCurrentPage(pageNumber)
    window?.scrollTo(0, 0)
    localStorage.setItem('isViewReport', 'false')
  }

  const paginationItems = useMemo(() => {
    if (paginationRange.length < 6) {
      return paginationRange
    }

    if (paginationRange.includes(currentPage + 4)) {
      return paginationRange.slice(currentPage - 1, currentPage + 4)
    } else {
      return paginationRange.slice(
        paginationRange.length - 5,
        paginationRange.length,
      )
    }
  }, [currentPage, paginationRange])

  return (
    <CPagination
      className={disabled ? 'opacity-50 pe-none' : ''}
      aria-disabled={disabled}
    >
      <CPaginationItem
        className="d-md-block d-none"
        disabled={currentPage === 1 || disabled}
        onClick={handleFirstPage}
        data-testid="first-page"
      >
        &laquo; First
      </CPaginationItem>
      <CPaginationItem
        disabled={currentPage === 1 || disabled}
        onClick={handlePreviousPage}
        data-testid="prev-page"
      >
        &#8249; Prev
      </CPaginationItem>
      {paginationItems.map((pageNumber, index) => {
        return (
          <CPaginationItem
            key={index}
            active={currentPage === pageNumber}
            disabled={disabled}
            onClick={() => handleSelectPage(pageNumber)}
            data-testid="page-number"
          >
            {pageNumber}
          </CPaginationItem>
        )
      })}
      <CPaginationItem
        onClick={handleNextPage}
        disabled={
          currentPage === paginationRange[paginationRange.length - 1] ||
          disabled
        }
        data-testid="next-page"
      >
        Next &#8250;
      </CPaginationItem>
      <CPaginationItem
        className="d-md-block d-none"
        disabled={
          currentPage === paginationRange[paginationRange.length - 1] ||
          disabled
        }
        onClick={handleLastPage}
        data-testid="last-page"
      >
        Last &raquo;
      </CPaginationItem>
    </CPagination>
  )
}

export default OPagination
